import React from 'react';
import { SkeletonButton, SkeletonLine, SkeletonMedia } from '../Skeleton';

type EventListSkeletonProps = {
  viewMode?: 'card' | 'list';
  count?: number;
  cardGridRef?: React.Ref<HTMLDivElement> | null;
  cardGridStyle?: React.CSSProperties;
  label?: string;
  showListControl?: boolean;
};

const normalizeCount = (count: unknown, fallback = 5) => Math.max(1, Number(count) || fallback);

export default function EventListSkeleton({
  viewMode = 'card',
  count = 5,
  cardGridRef = null,
  cardGridStyle,
  label = 'イベント情報を読み込み中...',
  showListControl = true,
}: EventListSkeletonProps) {
  const skeletonItems = Array.from({ length: normalizeCount(count) }, (_, index) => index);

  if (viewMode === 'list') {
    return (
      <div className="event-list-rows event-list-rows--skeleton" role="status" aria-live="polite">
        {skeletonItems.map((index) => (
          <article
            key={`event-row-skeleton-${index}`}
            className="event-list-row event-list-row--skeleton"
            aria-hidden="true"
            style={{ '--skeleton-index': index } as React.CSSProperties}
          >
            <div className="event-list-row__content">
              <div className="event-list-row__main">
                <SkeletonMedia className="event-skeleton--row-flyer" />
                <div className="event-list-row__date">
                  <SkeletonLine className="event-skeleton--date-day" />
                </div>
                <div className="event-list-row__info">
                  <SkeletonLine className="event-skeleton--w70" />
                  <SkeletonLine className="event-skeleton--w46" />
                  <SkeletonLine className="event-skeleton--w58" />
                </div>
              </div>
              <div className="event-list-row__aside">
                <SkeletonLine className="event-skeleton--w100" />
                <SkeletonLine className="event-skeleton--w88" />
                {showListControl ? <SkeletonButton className="event-skeleton--control" /> : null}
              </div>
            </div>
          </article>
        ))}
        <span className="sr-only">{label}</span>
      </div>
    );
  }

  return (
    <div
      ref={cardGridRef}
      className="event-list event-list--skeleton"
      role="status"
      aria-live="polite"
      style={cardGridStyle}
    >
      {skeletonItems.map((index) => (
        <article
          key={`event-card-skeleton-${index}`}
          className="event-card event-card--skeleton"
          aria-hidden="true"
          style={{ '--skeleton-index': index } as React.CSSProperties}
        >
          <SkeletonMedia className="event-skeleton--media" />
          <div className="event-card-body">
            <div className="event-card-header">
              <div className="event-card-title-block">
                <SkeletonLine className="event-skeleton--w80" />
                <SkeletonLine className="event-skeleton--w55" />
              </div>
              <div className="event-card-date">
                <SkeletonLine className="event-skeleton--date-year" />
                <SkeletonLine className="event-skeleton--date-day" />
                <SkeletonLine className="event-skeleton--date-week" />
              </div>
              <SkeletonLine className="event-skeleton--w70" />
              <SkeletonLine className="event-skeleton--w62" />
            </div>
            <div className="event-card-progress">
              <SkeletonLine className="event-skeleton--w100" />
              <SkeletonLine className="event-skeleton--w88" />
              <SkeletonLine className="event-skeleton--w92" />
            </div>
          </div>
        </article>
      ))}
      <span className="sr-only">{label}</span>
    </div>
  );
}
